/**
 * "Bold Modern" — high-contrast poster look. Full-bleed photo with a solid
 * black caption block bottom-left; big uppercase display type; a single
 * accent line in warm yellow. Premium template.
 */

import { SLIDE_W, SLIDE_H, type CarouselTemplate, type CoverSlideData, type SpeciesSlideData, type ClosingSlideData, type SlideStyle } from "../types";
import { drawCoverFitImage, drawReadabilityGradient, drawWatermark, truncateToWidth, wrapTopDown, FLAT_BG_COLORS } from "../shared";

const BLACK = "#0d0d0b";
const WHITE = "#f5f4ef";
const ACCENT = "#e8c547";

function drawCover(ctx: CanvasRenderingContext2D, data: CoverSlideData, style: SlideStyle): void {
  const W = SLIDE_W, H = SLIDE_H, PAD = 56;

  ctx.clearRect(0, 0, W, H);
  // The bottom caption block below is always solid black regardless of any
  // of this — only the top area (and its kicker text) responds to bg mode.
  const flatKey = !data.photo && (data.backgroundMode === "light" || data.backgroundMode === "dark" || data.backgroundMode === "accent")
    ? data.backgroundMode
    : null;
  const kickerOnLight = flatKey === "light";

  if (data.photo || (!flatKey && data.backgroundMode !== "transparent")) {
    ctx.fillStyle = BLACK;
    ctx.fillRect(0, 0, W, H);
  } else if (flatKey) {
    ctx.fillStyle = FLAT_BG_COLORS[flatKey];
    ctx.fillRect(0, 0, W, H);
  } else {
    // transparent, no photo — top kicker needs its own contrast band.
    drawReadabilityGradient(ctx);
  }

  if (data.photo) {
    drawCoverFitImage(ctx, data.photo, data.photoOffsetY ?? 0, data.photoZoom ?? 1);
    // darken bottom half so the block edge reads
    const grad = ctx.createLinearGradient(0, H * 0.45, 0, H);
    grad.addColorStop(0, "rgba(13,13,11,0)");
    grad.addColorStop(1, "rgba(13,13,11,0.55)");
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, W, H);
  }

  // Kicker chip — accent bar + uppercase label, top-left
  ctx.fillStyle = ACCENT;
  ctx.fillRect(PAD, PAD, 10, 44);
  ctx.font = `600 32px ${style.fonts.body}`;
  ctx.fillStyle = kickerOnLight ? BLACK : WHITE;
  ctx.textAlign = "left";
  ctx.textBaseline = "top";
  ctx.fillText(data.kickerText.toUpperCase(), PAD + 30, PAD + 4);

  // Bottom block: headline + subtitle + stats on solid black
  const blockTop = H - 470;
  ctx.fillStyle = BLACK;
  ctx.fillRect(0, blockTop, W, H - blockTop);
  ctx.fillStyle = ACCENT;
  ctx.fillRect(0, blockTop, W, 8);

  const titleSize = Math.min(data.headlineFontSize, 116);
  ctx.font = `600 ${titleSize}px ${style.fonts.display}`;
  ctx.fillStyle = WHITE;
  const afterTitle = wrapTopDown(ctx, data.headlineTitle.toUpperCase(), PAD, blockTop + 52, W - PAD * 2, titleSize * 1.12, 2);

  ctx.font = `400 32px ${style.fonts.body}`;
  ctx.fillStyle = "rgba(245,244,239,0.60)";
  ctx.fillText(data.subtitle, PAD, afterTitle + 14);

  if (data.stats.length > 0) {
    const statY = H - 78;
    const segW = (W - PAD * 2) / data.stats.length;
    ctx.textBaseline = "alphabetic";
    data.stats.forEach((seg, i) => {
      const x = PAD + i * segW;
      ctx.font = `600 28px ${style.fonts.body}`;
      ctx.fillStyle = WHITE;
      ctx.fillText(truncateToWidth(ctx, seg.value, segW - 18), x, statY);
    });
  }

  drawWatermark(ctx, style);
}

function drawSpecies(ctx: CanvasRenderingContext2D, data: SpeciesSlideData, style: SlideStyle): void {
  const W = SLIDE_W, H = SLIDE_H, PAD = 56;

  ctx.fillStyle = BLACK;
  ctx.fillRect(0, 0, W, H);

  if (data.photo) drawCoverFitImage(ctx, data.photo, data.photoOffsetY ?? 0, data.photoZoom ?? 1);

  // Solid black caption block, bottom-left, sized to content
  const lines: { text: string; font: string; color: string; h: number }[] = [];
  if (data.namePrimary) lines.push({ text: data.namePrimary.toUpperCase(), font: `600 58px ${style.fonts.display}`, color: WHITE, h: 84 });
  if (data.nameSecondary) lines.push({ text: data.nameSecondary, font: `400 34px ${style.fonts.body}`, color: "rgba(245,244,239,0.85)", h: 54 });
  lines.push({ text: data.nameTertiary, font: `400 30px ${style.fonts.body}`, color: "rgba(245,244,239,0.60)", h: 50 });

  const blockPadX = 40, blockPadY = 36;
  let maxTextW = 0;
  for (const l of lines) {
    ctx.font = l.font;
    maxTextW = Math.max(maxTextW, Math.min(ctx.measureText(l.text).width, W - PAD * 2 - blockPadX * 2));
  }
  const blockH = lines.reduce((sum, l) => sum + l.h, 0) + blockPadY * 2;
  const blockW = maxTextW + blockPadX * 2;
  const blockX = PAD, blockY = H - PAD - blockH;

  ctx.fillStyle = BLACK;
  ctx.fillRect(blockX, blockY, blockW, blockH);
  ctx.fillStyle = ACCENT;
  ctx.fillRect(blockX, blockY, 10, blockH);

  ctx.textAlign = "left";
  ctx.textBaseline = "middle";
  let y = blockY + blockPadY;
  for (const l of lines) {
    ctx.font = l.font;
    ctx.fillStyle = l.color;
    ctx.fillText(truncateToWidth(ctx, l.text, W - PAD * 2 - blockPadX * 2), blockX + blockPadX + 14, y + l.h / 2);
    y += l.h;
  }

  ctx.textBaseline = "alphabetic";
  drawWatermark(ctx, style);
}

function drawClosing(ctx: CanvasRenderingContext2D, data: ClosingSlideData, style: SlideStyle): void {
  const W = SLIDE_W, H = SLIDE_H, PAD = 56;

  ctx.fillStyle = BLACK;
  ctx.fillRect(0, 0, W, H);

  if (data.photo) {
    drawCoverFitImage(ctx, data.photo, data.photoOffsetY ?? 0, data.photoZoom ?? 1);
    ctx.fillStyle = "rgba(13,13,11,0.72)";
    ctx.fillRect(0, 0, W, H);
  }

  ctx.textAlign = "left";
  ctx.textBaseline = "top";

  ctx.fillStyle = ACCENT;
  ctx.fillRect(PAD, H / 2 - 220, 12, 120);

  ctx.font = `600 96px ${style.fonts.display}`;
  ctx.fillStyle = WHITE;
  const afterHeading = wrapTopDown(ctx, data.heading.toUpperCase(), PAD, H / 2 - 110, W - PAD * 2, 108, 2);

  ctx.font = `400 34px ${style.fonts.body}`;
  ctx.fillStyle = "rgba(245,244,239,0.65)";
  ctx.fillText(data.subtext, PAD, afterHeading + 24);

  if (data.handle) {
    ctx.font = `600 44px ${style.fonts.body}`;
    ctx.fillStyle = ACCENT;
    ctx.fillText(data.handle, PAD, afterHeading + 110);
  }

  drawWatermark(ctx, style);
}

export const boldModern: CarouselTemplate = {
  id: "bold-modern",
  name: "Bold Modern",
  premium: true,
  drawCover,
  drawSpecies,
  drawClosing,
};
